import { useRef, type ReactNode } from 'react'
import { useGsapContext } from '../../hooks/useGsapReveal'
import {
  driftX,
  splitLinesReveal,
  staggerChildren,
} from '../../animations/gsapAnimations'

type PageIntroProps = {
  eyebrow: string
  title: ReactNode
  lede?: string
  /** Oversized ghost lettering behind the masthead. Defaults to the eyebrow. */
  ghost?: string
  children?: ReactNode
}

/* The masthead every inner page opens with. Carries the top padding that
   clears the fixed navbar, so pages never repeat that magic number.

   Behind the copy, the page's own name is embossed across the band as ghost
   lettering — stroke, no fill — drifting slowly against the scroll the way
   a model name runs along a showroom wall. It is architecture, not copy:
   aria-hidden, clipped, and faint enough to read second.

   It is above the fold on arrival, so nothing here waits for a scroll: the
   title's lines rise out of their masks while the supporting copy fades up
   underneath, offset just enough to read as one movement rather than two. */
function PageIntro({ eyebrow, title, lede, ghost, children }: PageIntroProps) {
  const titleRef = useRef<HTMLHeadingElement>(null)

  const scope = useGsapContext<HTMLElement>((element) => {
    staggerChildren(element, '[data-intro]', {
      y: 18,
      delay: 0.12,
      stagger: 0.1,
      start: 'top 98%',
    })

    const ghostEl = element.querySelector('[data-ghost]')
    if (ghostEl) driftX(ghostEl, 10, element)

    /* Returned so the hook can revert SplitText's markup on unmount — a
       route change here is a full remount. */
    return titleRef.current
      ? splitLinesReveal(titleRef.current, { scroll: false, stagger: 0.09 })
      : undefined
  })

  return (
    <section
      ref={scope}
      className="relative overflow-hidden border-b border-line-soft band-masthead px-5 pt-[136px] pb-16 sm:px-8 sm:pt-[168px] sm:pb-20"
    >
      <span
        aria-hidden="true"
        data-ghost
        className="pointer-events-none absolute -top-4 left-0 font-display text-[clamp(6rem,20vw,17rem)] leading-none font-bold tracking-[-0.02em] whitespace-nowrap text-ghost select-none"
      >
        {ghost ?? eyebrow}
      </span>

      <div className="relative mx-auto w-full max-w-[1320px]">
        <p data-intro className="flex items-center gap-3 label text-lime">
          <span aria-hidden="true" className="h-px w-9 coachline" />
          {eyebrow}
        </p>
        <h1
          ref={titleRef}
          className="mt-6 max-w-[20ch] font-display text-[clamp(2rem,4.6vw,3.5rem)] leading-[1.05] font-semibold tracking-[-0.03em] text-platinum"
        >
          {title}
        </h1>
        {lede ? (
          <p
            data-intro
            className="mt-6 max-w-[54ch] text-[15.5px] leading-relaxed text-steel"
          >
            {lede}
          </p>
        ) : null}
        {children ? (
          <div data-intro className="mt-10">
            {children}
          </div>
        ) : null}
      </div>
    </section>
  )
}

export default PageIntro
